<script setup>
import YandexDirectSVG from "@/components/UI/Logos/YandexDirectSVG.vue";
import {ref, defineEmits, defineModel, watchEffect} from "vue";
import SelectYandexAccountModal from "@/components/YandexDirectStatistics/SelectYandexAccountModal.vue";
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { ru } from 'date-fns/locale'
import ErrorTriangleSvg from "@/components/UI/svg/ErrorTriangleSvg.vue";
import DropdownComponent from "@/components/UI/DropdownComponent.vue";
import ConnectionSourceCard from "@/components/CreateConnectionComponents/ConnectionSourceCard.vue";

const isAccModalVisible = ref(false)
const selectedAccount = defineModel('account')

const emits = defineEmits(['next-step', 'change-step'])

function toggleAccModal() {
    isAccModalVisible.value = !isAccModalVisible.value
}

const uploadOptions = ref({
    offsetOperation: [
        {
            name: 'Плюс',
            type: 'PLUS'
        },
        {
            name: 'Минус',
            type: 'MINUS'
        },
    ],
    offsetNames: [
        {
            name: 'Дней',
            type: 'DAY'
        },
        {
            name: 'Месяцев',
            type: 'MONTH'
        },
    ]
})

const startExactDate = ref({
    type: "exact",
    date: null
})

const startOffsetDate = ref({
    type: "offset",
    length: 0,
    offsetOperation: uploadOptions.value.offsetOperation[0],
    offsetName: uploadOptions.value.offsetNames[0]
})

const endExactDate = ref({
    type: "exact",
    date: null
})

const endOffsetDate = ref({
    type: "offset",
    length: 0,
    offsetOperation: uploadOptions.value.offsetOperation[0],
    offsetName: uploadOptions.value.offsetNames[0]
})

const timeOffsetStart = ref(0)
const timeOffsetEnd = ref(0)

const startDate = defineModel('startDate')
const endDate = defineModel('endDate')

const errorMessage = ref("")

const handleInputStart = (event) => {
    const value = event.target.value
    const numericValue = value.replace(/[^\d]/g, '')
    const limitedValue = numericValue.slice(0, 4)
    timeOffsetStart.value = limitedValue === '' ? 0 : Number(limitedValue)
}

const handleInputEnd = (event) => {
    const value = event.target.value
    const numericValue = value.replace(/[^\d]/g, '')
    const limitedValue = numericValue.slice(0, 4)
    timeOffsetEnd.value = limitedValue === '' ? 0 : Number(limitedValue)
}

function getDate(date) {
    if (!date) return null

    if (date.type === 'offset') {
        const today = new Date()
        const result = new Date(today) // Создаем копию

        if (date.length === "") return null
        // Определяем направление смещения
        const multiplier = date.offsetOperation?.type === 'MINUS' ? -1 : 1

        if (date.offsetName?.type === 'MONTH') {
            result.setMonth(result.getMonth() + (date.length * multiplier))
            return result
        }
        if (date.offsetName?.type === 'DAY') {
            result.setDate(result.getDate() + (date.length * multiplier))
            return result
        }
    }

    if (date.type === 'exact') {
        return date.date ? new Date(date.date) : null
    }

    return null
}
const isDataInputRight = ref(false)


watchEffect(() => {
    if (selectedAccount.value === null) {
        errorMessage.value = "Выберите аккаунт"
        isDataInputRight.value = false
        return
    }
    if (startDate.value === null || endDate.value == null) {
        isDataInputRight.value = false
        return
    }
    const start = getDate(startDate.value)
    const end = getDate(endDate.value)

    if (start === null || end === null) {
        errorMessage.value = "Выберите период выгрузки"
        isDataInputRight.value = false
        return
    }
    if (end < start) {
        console.log(new Date(end))
        console.log(new Date(start))
        errorMessage.value = "Дата окончания предшествует дате начала"
        isDataInputRight.value = false
        return
    }
    isDataInputRight.value = true;

})


function updateAccount(account) {
    selectedAccount.value = account
}

function nextStep() {
    emits('next-step')
}

</script>

<template>
    <ConnectionSourceCard
        title="Статистика"
        subtitle="Yandex Direct"
        show-next-step
        :next-step-enabled="isDataInputRight"
        @change-step="emits('change-step')"
        @next-step="nextStep"
    >
        <template #icon>
            <YandexDirectSVG class="w-full h-full" />
        </template>

                <div class="mt-6 mb-3 sm:mb-4 font-bold text-base sm:text-lg">
                    Выбрать аккаунт для выгрузки статистики
                </div>
                <div class="rounded-md border flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-3 sm:py-3 w-full gap-4 sm:gap-0">
                    <div class="flex items-center">
                        <div class="flex items-center gap-4">
                            <div class="flex items-center gap-4">
                                <div v-if="selectedAccount && selectedAccount?.default_avatar_id" class="size-12 sm:size-16 md:size-20 rounded-full flex-shrink-0 overflow-hidden">
                                    <img
                                        :src="'https://avatars.yandex.net/get-yapic/' + selectedAccount.default_avatar_id + '/islands-200'"
                                        :alt="selectedAccount.display_name"
                                        class="w-full h-full object-cover"
                                    />
                                </div>
                                <div v-else class="size-12 sm:size-16 md:size-20 rounded-full bg-blue-500 flex-shrink-0"></div>
                            </div>
                        </div>

                        <div v-if="selectedAccount !== null" class="font-bold text-sm sm:text-base ml-2 sm:ml-3 truncate">
                            {{ selectedAccount?.display_name }}
                        </div>
                        <div v-else class="font-bold text-sm sm:text-base ml-2 sm:ml-3 truncate">
                            Аккаунт не выбран
                        </div>
                    </div>
                    <div
                        @click="toggleAccModal"
                        class="cursor-pointer mr-10 bg-blue-500 font-bold text-white px-4 py-2 sm:px-3 sm:py-0 flex items-center justify-center h-10 rounded-lg hover:bg-blue-600 duration-150 transition-all text-sm sm:text-base whitespace-nowrap"
                    >
                        Выбрать аккаунт
                    </div>
                </div>

            <div class="w-full mt-8 sm:mt-10">
                <div class="mb-3 sm:mb-4 font-bold text-base sm:text-lg">
                    Параметры выгрузки статистики
                </div>
                <div class="flex flex-col lg:flex-row gap-8 lg:gap-12 xl:gap-16">
                    <div class="flex-1 min-w-0">
                        <div class="font-bold mb-4 sm:mb-5 text-base">Начало</div>
                        <div class="flex flex-col">
                            <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:gap-10 mb-6 sm:mb-8 lg:mb-10">
                                <div class="flex items-center w-fit whitespace-nowrap">
                                    <input
                                        id="start-radio-1"
                                        type="radio"
                                        :value="startExactDate"
                                        :checked="startDate.type === startExactDate.type"
                                        v-model="startDate"
                                        class="w-4 h-4"
                                    >
                                    <label for="start-radio-1" class="select-none ms-2 text-sm sm:text-base font-medium text-heading">
                                        Точная дата
                                    </label>
                                </div>
                                <VueDatePicker
                                    v-show="startDate.type === 'exact'"
                                    v-model="startDate.date"
                                    :formats="{ input: 'dd.MM.yyyy' }"
                                    :time-config="{ enableTimePicker: false }"
                                    :locale="ru"
                                    :disabled="startDate.type !== startExactDate.type"
                                    class="w-full sm:w-36"
                                    auto-apply
                                />
                            </div>

                            <div class="flex items-center mb-4">
                                <input
                                    id="start-radio-2"
                                    type="radio"
                                    :value="startOffsetDate"
                                    :checked="startDate.type === startOffsetDate.type"
                                    v-model="startDate"
                                    class="w-4 h-4"
                                >
                                <label for="start-radio-2" class="select-none ms-2 text-sm sm:text-base font-medium text-heading">
                                    Смещение от текущей даты
                                </label>
                            </div>

                            <div class="flex flex-col sm:flex-row gap-2" v-show="startDate.type==='offset'">
                                <DropdownComponent v-model:value="startDate.offsetOperation" :values="uploadOptions.offsetOperation" label-key="name" />
                                <input
                                    v-model="startDate.length"
                                    @input="handleInputStart"
                                    class="w-16 px-3 py-2 border rounded-lg text-sm sm:text-sm text-right bg-white flex justify-between items-center"
                                    placeholder="90"
                                >
                                <DropdownComponent v-model:value="startDate.offsetName" :values="uploadOptions.offsetNames" label-key="name" />
                            </div>
                        </div>
                    </div>

                    <div class="flex-1 min-w-0">
                        <div class="font-bold mb-4 sm:mb-5 text-base">Конец</div>
                        <div class="flex flex-col">
                            <div class="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:gap-10 mb-6 sm:mb-8 lg:mb-10">
                                <div class="flex items-center w-fit whitespace-nowrap">
                                    <input
                                        id="end-radio-1"
                                        type="radio"
                                        :value="endExactDate"
                                        :checked="endDate.type === endExactDate.type"
                                        v-model="endDate"
                                        class="w-4 h-4"
                                    >
                                    <label for="end-radio-1" class="select-none ms-2 text-sm sm:text-base font-medium text-heading">
                                        Точная дата
                                    </label>
                                </div>
                                <VueDatePicker
                                    v-show="endDate.type === 'exact'"
                                    v-model="endDate.date"
                                    :formats="{ input: 'dd.MM.yyyy' }"
                                    :time-config="{ enableTimePicker: false }"
                                    :locale="ru"
                                    :disabled="endDate.type !== endExactDate.type"
                                    class="w-full sm:w-36"
                                    auto-apply
                                />
                            </div>

                            <div class="flex items-center mb-4">
                                <input
                                    id="end-radio-2"
                                    type="radio"
                                    :value="endOffsetDate"
                                    :checked="endDate.type === endOffsetDate.type"
                                    v-model="endDate"
                                    class="w-4 h-4"
                                >
                                <label for="end-radio-2" class="select-none ms-2 text-sm sm:text-base font-medium text-heading">
                                    Смещение от текущей даты
                                </label>
                            </div>

                            <div class="flex flex-col sm:flex-row gap-2" v-show="endDate.type==='offset'">
                                <DropdownComponent v-model:value="endDate.offsetOperation" :values="uploadOptions.offsetOperation" label-key="name" />
                                <input
                                    v-model="endDate.length"
                                    @input="handleInputEnd"
                                    class="w-16 px-3 py-2 border rounded-lg text-sm sm:text-sm text-right bg-white flex justify-between items-center"
                                    placeholder="90"
                                >
                                <DropdownComponent v-model:value="endDate.offsetName" :values="uploadOptions.offsetNames" label-key="name" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-5 mt-8" v-show="!isDataInputRight">
                <ErrorTriangleSvg class="size-6 stroke-white fill-red-500"/>
                <div>
                    {{ errorMessage }}
                </div>
            </div>
    </ConnectionSourceCard>

    <SelectYandexAccountModal
        v-model:is-visible="isAccModalVisible"
        @update:account="updateAccount"
    />
</template>

<style scoped>
input[type="radio"] {
    @apply cursor-pointer;
}

select {
    @apply cursor-pointer bg-white;
}

@media (max-width: 480px) {
    .text-sm {
        font-size: 13px;
    }

    .size-12 {
        width: 2.5rem;
        height: 2.5rem;
    }

    .px-4 {
        padding-left: 1rem;
        padding-right: 1rem;
    }
}
</style>